/**
 * Auth service — обёртки над Apollo-мутациями для auth-флоу.
 *
 * Phone+SMS работает всегда. Apple/Google активны только при заданных
 * VITE_APPLE_CLIENT_ID / VITE_GOOGLE_CLIENT_ID (см. social-auth.service).
 */

import { apolloClient } from '@app/services/apollo-client';
import {
  GetSmsCodeDocument,
  AuthenticateDocument,
  AppleAuthDocument,
  GoogleAuthDocument,
  type GetSmsCodeQueryVariables,
  type AuthenticateMutationVariables,
} from '@app/api/generated/graphql';
import { signInWithApple, signInWithGoogle } from '@app/services/social-auth.service';

export interface SendCodeResult {
  expiresAt: string;
}

export interface AuthResult {
  accessToken: string;
  refreshToken: string;
  status: string;
}

export const authService = {
  /** Отправить SMS-код на номер. */
  async sendCode(phoneNumber: string): Promise<SendCodeResult> {
    const variables: GetSmsCodeQueryVariables = {
      phoneNumber,
      service: 'AURA', // см. ios AuthService: service = "AURA"
    };
    const { data, errors } = await apolloClient.query({
      query: GetSmsCodeDocument,
      variables,
      fetchPolicy: 'network-only',
    });
    if (errors?.length) throw errors[0];
    return { expiresAt: data.sendCode.expiresAt };
  },

  /** Аутентифицироваться по коду. */
  async authenticate(
    phoneNumber: string,
    code: number,
    referralCode?: string
  ): Promise<AuthResult> {
    const variables: AuthenticateMutationVariables = {
      phoneNumber,
      code,
      referralCode,
    };
    const { data, errors } = await apolloClient.mutate({
      mutation: AuthenticateDocument,
      variables,
    });
    if (errors?.length) throw errors[0];
    if (!data?.authenticate) throw new Error('Empty authenticate response');
    return data.authenticate;
  },

  /** Вход через Apple: popup → identityToken → мутация appleAuth. */
  async authenticateWithApple(referralCode?: string): Promise<AuthResult> {
    const credential = await signInWithApple();
    const { data, errors } = await apolloClient.mutate({
      mutation: AppleAuthDocument,
      variables: {
        appleToken: credential.token,
        ref: referralCode,
        // Apple отдаёт имя только при первом входе — дальше поля пустые.
        firstname: credential.firstname,
        lastname: credential.lastname,
      },
    });
    if (errors?.length) throw errors[0];
    if (!data?.appleAuth) throw new Error('Empty appleAuth response');
    return data.appleAuth;
  },

  /** Вход через Google: popup → access_token → мутация googleAuth. */
  async authenticateWithGoogle(referralCode?: string): Promise<AuthResult> {
    const accessToken = await signInWithGoogle();
    const { data, errors } = await apolloClient.mutate({
      mutation: GoogleAuthDocument,
      variables: { googleAccessToken: accessToken, ref: referralCode },
    });
    if (errors?.length) throw errors[0];
    if (!data?.googleAuth) throw new Error('Empty googleAuth response');
    return data.googleAuth;
  },
};
